import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { PaymentRequest } from '../../../types/payment/payment-request';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgOptimizedImage } from '@angular/common';
import { Pay } from '../../../types/payment/pay';
import { HttpPaymentService } from '../../../apis/http-payment.service';
import { Router } from '@angular/router';
import { SignalrPaymentStatusService } from '../../../apis/signalr-payment-status.service';
import { PaymentRequestStatus } from '../../../types/payment/payment-request-status';
import { PaymentProcessingStatus } from '../../../types/payment/payment-processing-status';
import { LoaderService } from '../../../core/services/loader.service';
import { AdjustableAmountComponent } from '../adjustable-amount/adjustable-amount.component';
import { trace, SpanStatusCode } from '@opentelemetry/api';
import { DomainEventBus, DomainEventType } from '../../../core/events/domain-event-bus';

@Component({
  selector: 'app-pay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, NgOptimizedImage, AdjustableAmountComponent],
  styleUrl: 'pay.component.less',
  templateUrl: 'pay.component.html'
})
export class PayComponent implements OnInit, OnDestroy {
  @Input() request!: PaymentRequest;
  @Output() newRequestStatus = new EventEmitter<PaymentRequestStatus>();
  private stopPolling: boolean = false;
  public disablePayButton: boolean = false;
  @ViewChild(AdjustableAmountComponent) adjustableAmount!: AdjustableAmountComponent;

  constructor(
    private formBuilder: FormBuilder,
    private api: HttpPaymentService,
    private router: Router,
    private notification: SignalrPaymentStatusService,
    private loader: LoaderService,
    private bus: DomainEventBus
  ) {}

  paymentForm = this.formBuilder.group({
    amount: []
  });

  ngOnInit(): void {
    this.bus.emit({ type: DomainEventType.PaymentPageReady, publicId: this.request?.publicId ?? '' });
    this.notification.connect();
  }

  ngOnDestroy(): void {
    this.stopPolling = true;
    this.notification.disconnect();
  }

  public async onPay() {
    if (this.paymentForm.invalid) {
      console.error('form is invalid');
      this.adjustableAmount.form.markAllAsTouched();
      return;
    }
    this.disablePayButton = true;
    this.loader.showLoader();
    let wholeAmount = this.adjustableAmount.form.controls.amount.value!;

    const pay: Pay = {
      transactionCorrelationId: crypto.randomUUID().replaceAll('-', ''),
      publicId: this.request!.publicId!,
      amount: Math.round(wholeAmount * 100) // Convert to cents
    };

    this.notification.subscribe(pay.transactionCorrelationId, (e) => {
      if (e.type === 'transactionInitialized') this.handlePaymentUpdateStatus(pay.transactionCorrelationId, PaymentProcessingStatus.pending, e.payload.status, e.payload.transactionPaymentUrl);
      if (e.type === 'transactionInitializationFailed') this.handlePaymentUpdateStatus(pay.transactionCorrelationId, PaymentProcessingStatus.failed, e.payload.status, e.payload.transactionPaymentUrl);
    });

    try {
      this.bus.emit({ type: DomainEventType.PaymentSubmit, publicId: pay.publicId, correlationId: pay.transactionCorrelationId });
      await this.api.pay(pay);
      this.notification.poll(
        async () => {
          const res = await this.api.getPaymentStatus(pay.publicId, pay.transactionCorrelationId);
          this.handlePaymentUpdateStatus(pay.transactionCorrelationId, res.paymentStatus, res.paymentRequestStatus, res.paymentUrl);
        },
        () => this.stopPolling
      );
    } catch (exception: any) {
      const responseBody = exception.error;
      if (responseBody?.paymentRequestStatus) {
        this.newRequestStatus.emit(responseBody.paymentRequestStatus as PaymentRequestStatus);
      } else {
        this.router.navigate([this.request.publicId, pay.transactionCorrelationId, 'processed', 'failed'], { replaceUrl: true });
      }
    }
  }

  private handlePaymentUpdateStatus(transactionCorrelationId: string, status: string, paymentRequestStatus: string, paymentUrl: string | null) {
    this.stopPolling = true;
    this.bus.emit({ type: DomainEventType.PaymentStatusFirstHandled, publicId: this.request.publicId, correlationId: transactionCorrelationId, status, requestStatus: paymentRequestStatus });
    if (paymentUrl && paymentRequestStatus === PaymentRequestStatus.open && status === PaymentProcessingStatus.pending) {
      this.bus.emit({ type: DomainEventType.PspRedirectHandoff, publicId: this.request.publicId, correlationId: transactionCorrelationId, url: paymentUrl });
      window.location.href = paymentUrl;
    } else if (paymentRequestStatus !== PaymentRequestStatus.open) {
      this.newRequestStatus.emit(paymentRequestStatus as PaymentRequestStatus);
    } else if (status !== PaymentProcessingStatus.pending) {
      this.router.navigate([this.request.publicId, transactionCorrelationId, 'processed', status], { replaceUrl: true });
    } else {
      this.router.navigate([this.request.publicId, transactionCorrelationId, 'processed', PaymentProcessingStatus.failed], { replaceUrl: true });
    }
  }
}
